import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import SummaryTable, { SummaryRow } from '../table/SummaryTable';
import { getFieldUpdate, getTranslatedValueWithFallback, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
}

export default function RiskAssessmentDetail({
  data,
  translation,
  currentLanguage,
  refetch,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_RISK_ASSESSMENT.${id}` });
  const emDash = '—';
  const emptySummary = t('SITE_UNAVAILABLE');

  const results = data;
  const summaryLevel = results?.result?.summary?.level ?? emDash;
  const summaryReason = results?.result?.summary?.ai_reason ?? emDash;
  const normalizedSummaryLevel =
    summaryLevel === emDash ? emDash : String(summaryLevel).toUpperCase();
  const translatedSummaryLevelMap: Record<string, string> = {
    LOW: t('LEVEL_LOW'),
    MEDIUM: t('LEVEL_MEDIUM'),
    'MEDIUM-HIGH': t('LEVEL_MEDIUM_HIGH'),
    HIGH: t('LEVEL_HIGH'),
  };
  const summaryLevelLabel =
    normalizedSummaryLevel === emDash
      ? emDash
      : translatedSummaryLevelMap[normalizedSummaryLevel] ?? normalizedSummaryLevel;
  const cardSchemeRisk = results?.result?.card_scheme_risk;

  const mastercardRiskReason = getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'card_scheme_risk.master_card_risk_reason',
    cardSchemeRisk?.master_card_risk_reason ?? emDash
  );
  
  const visaRiskReason = getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'card_scheme_risk.visa_card_risk_reason',
    cardSchemeRisk?.visa_card_risk_reason ?? emDash
  );

  const cardSchemeFindings = useMemo(() => {
    const reasons = [mastercardRiskReason, visaRiskReason]
      .map((reason) => (typeof reason === 'string' ? reason.trim() : ''))
      .filter((reason) => reason.length > 0 && reason !== emDash);

    return Array.from(new Set(reasons));
  }, [mastercardRiskReason, visaRiskReason, emDash]);

  const levelClassName = clsx(
    'fw-bolder fs-2 text-end',
    intl.locale === 'en' ? 'px-6' : 'px-12',
    {
      'text-success': normalizedSummaryLevel === 'LOW',
      'text-warning': ['MEDIUM', 'MEDIUM-HIGH'].includes(normalizedSummaryLevel),
      'text-danger': normalizedSummaryLevel === 'HIGH',
      'text-gray-600': !['LOW', 'MEDIUM', 'MEDIUM-HIGH', 'HIGH'].includes(normalizedSummaryLevel),
    }
  );

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
      >
        <div className='p-9 vstack gap-9'>
          <div className='d-flex flex-md-row align-items-md-center justify-content-between gap-6'>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-5 fs-6 lh-lg text-gray-700">
                <span className="text-primary flex-shrink-0">
                  <i className="bi bi-magic fs-4"></i>
                </span>

                <span>{getTranslatedValueWithFallback(
                  translation,
                  currentLanguage,
                  'summary.ai_reason',
                  summaryReason === emDash ? emptySummary : summaryReason
                )}</span>
              </div>
            </div>

            <div className='border border-gray-300 rounded-4 bg-secondary bg-opacity-20 py-3 min-w-150px text-center'>
              <div className='text-uppercase fw-bolder fs-4 text-gray-500 mb-2 ls-1'>
                {t('RISK_PROFILE')}
              </div>
              <div className={levelClassName}>{summaryLevelLabel}</div>
            </div>
          </div>

          <div className='border border-gray-300 rounded-4 p-6'>
            <div className='text-uppercase fw-bolder fs-6 text-gray-500 mb-5 ls-2'>
              {t('CARD_SCHEME_RISK_ANALYSIS_AND_KEY_FINDINGS')}
            </div>

            {cardSchemeFindings.length > 0 ? (
              <ul className='mb-0 ps-7 text-gray-700 fs-6 lh-lg'>
                {cardSchemeFindings.map((finding, index) => (
                  <li key={`${finding}-${index}`}>{finding}</li>
                ))}
              </ul>
            ) : (
              <div className='text-gray-600 fs-6'>{emDash}</div>
            )}
          </div>
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>
    </>
  );
}
